// Real Firebase Feedback Service

import {
  collection,
  doc,
  getDoc,
  getDocs,
  setDoc,
  query,
  where,
  orderBy,
  serverTimestamp,
  Timestamp,
} from 'firebase/firestore';
import { FirebaseError } from 'firebase/app';
import { db } from './firebase';

export interface Feedback {
  id: string;
  eventId: string;
  attendeeId: string;
  attendeeName?: string;
  rating: number;
  message: string;
  createdAt: Date;
}

function docToFeedback(docSnap: any): Feedback {
  const data = docSnap.data();
  return {
    id: docSnap.id,
    eventId: data.eventId,
    attendeeId: data.attendeeId,
    attendeeName: data.attendeeName || undefined,
    rating: data.rating,
    message: data.message,
    createdAt: data.createdAt instanceof Timestamp ? data.createdAt.toDate() : new Date(data.createdAt),
  };
}

// One per attendee per event (ITER-199): the rules refuse a second create on this id
export function feedbackDocId(eventId: string, attendeeId: string): string {
  return `${eventId}_${attendeeId}`;
}

export async function submitFeedback(
  eventId: string,
  attendeeId: string,
  rating: number,
  message: string,
  attendeeName?: string
): Promise<Feedback> {
  if (rating < 1 || rating > 5) throw new Error('Rating must be between 1 and 5');

  const id = feedbackDocId(eventId, attendeeId);
  const feedbackRef = doc(db, 'feedback', id);
  try {
    await setDoc(feedbackRef, {
      eventId,
      attendeeId,
      attendeeName: attendeeName || null,
      rating,
      message: message.trim(),
      createdAt: serverTimestamp(),
    });
  } catch (error) {
    if (error instanceof FirebaseError && error.code === 'permission-denied') {
      const existing = await getDoc(feedbackRef);
      if (existing.exists()) throw new Error("You've already sent feedback for this event.");
    }
    throw error;
  }

  return {
    id,
    eventId,
    attendeeId,
    attendeeName,
    rating,
    message: message.trim(),
    createdAt: new Date(),
  };
}

export async function getFeedbackByEventId(eventId: string): Promise<Feedback[]> {
  const q = query(
    collection(db, 'feedback'),
    where('eventId', '==', eventId),
    orderBy('createdAt', 'desc')
  );
  const snapshot = await getDocs(q);
  return snapshot.docs.map(docToFeedback);
}

export async function hasSubmittedFeedback(eventId: string, attendeeId: string): Promise<boolean> {
  const q = query(
    collection(db, 'feedback'),
    where('eventId', '==', eventId),
    where('attendeeId', '==', attendeeId)
  );
  const snapshot = await getDocs(q);
  return !snapshot.empty;
}

export async function getFeedbackStats(eventId: string): Promise<{
  count: number;
  averageRating: number;
  ratingDistribution: Record<number, number>;
}> {
  const feedbacks = await getFeedbackByEventId(eventId);

  if (feedbacks.length === 0) {
    return { count: 0, averageRating: 0, ratingDistribution: { 1: 0, 2: 0, 3: 0, 4: 0, 5: 0 } };
  }

  const totalRating = feedbacks.reduce((sum, f) => sum + f.rating, 0);
  const ratingDistribution: Record<number, number> = { 1: 0, 2: 0, 3: 0, 4: 0, 5: 0 };
  feedbacks.forEach(f => { ratingDistribution[f.rating] = (ratingDistribution[f.rating] || 0) + 1; });

  return {
    count: feedbacks.length,
    averageRating: Math.round((totalRating / feedbacks.length) * 10) / 10,
    ratingDistribution,
  };
}

export async function getAttendeeFeedback(eventId: string, attendeeId: string): Promise<Feedback | null> {
  const snapshot = await getDoc(doc(db, 'feedback', feedbackDocId(eventId, attendeeId)));
  if (!snapshot.exists()) return null;
  return docToFeedback(snapshot);
}

export async function clearEventFeedback(eventId: string): Promise<void> {
  const q = query(collection(db, 'feedback'), where('eventId', '==', eventId));
  const snapshot = await getDocs(q);
  const { writeBatch } = await import('firebase/firestore');
  const batch = writeBatch(db);
  snapshot.docs.forEach(d => batch.delete(d.ref));
  await batch.commit();
}
